import { useCallback } from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';

import LocationSelector from '@src/components/LocationSelector';
import { useActiveOrg, usePermissions, useSidebar } from '@src/context/hooks';
import { useMeQuery } from '@src/query';

import SidebarActionItem from './SidebarActionItem';
import SidebarContainer from './SidebarContainer';
import SidebarDrawer from './SidebarDrawer';
import SidebarGroup from './SidebarGroup';
import SidebarNavItem from './SidebarNavItem';
import SidebarOrgSwitcher from './SidebarOrgSwitcher';
import SidebarUser from './SidebarUser';


function renderItem(item) {
  return (
    <SidebarNavItem
      key={item.key}
      exact={item.exact}
      label={item.label}
      disabled={item.disabled}
      to={item.to}
      icon={item.icon}
    />
  );
}


/** App sidebar per the .pen reusable Sidebar component: brand + org switcher on
 * top, nav (divider before Settings), user footer at the bottom. */
function Sidebar() {
  const sidebar = useSidebar();
  const { data: me } = useMeQuery();
  const { membership, memberships, seller, setActiveMembership } = useActiveOrg();
  const { isLister } = usePermissions();

  // Sales and Customers are financially gated (M8 #141, M16): a Lister gets 403
  // on every /sales/ and /customers/ endpoint, so hide the nav items rather than
  // route them to a no-access page.
  const topItems = isLister
    ? sidebar.items.top.filter((item) => item.key !== 'sales' && item.key !== 'customers')
    : sidebar.items.top;

  const handleOnToggle = useCallback(() => {
    sidebar.toggle();
  }, [sidebar]);

  return (
    <SidebarDrawer onClose={handleOnToggle} open={!sidebar.isCollapsed}>
      <SidebarContainer>
        <SidebarOrgSwitcher
          membership={membership}
          memberships={memberships}
          onSelect={setActiveMembership}
        />

        {/* Phone drawer also carries the location switcher (the M Nav Drawer
            shows org + location). The desktop rail has no location switcher, so
            gate it to the mobile drawer only. */}
        <LocationSelector seller={seller} className="self-start lg:hidden" />

        <SidebarGroup className="mt-2 flex-1">
          {topItems.map(renderItem)}
        </SidebarGroup>

        <SidebarGroup className="border-t border-hairline pt-2">
          {sidebar.items.bottom.map(renderItem)}

          {/* Desktop-rail collapse affordance only — the mobile drawer closes
              via the scrim/menu button, so hide it under lg:. */}
          <SidebarActionItem
            className="max-lg:hidden"
            label={sidebar.isCollapsed ? 'Show Panel' : 'Hide Panel'}
            onClick={handleOnToggle}
            icon={sidebar.isCollapsed ? <ChevronRight /> : <ChevronLeft />}
          />
        </SidebarGroup>

        <SidebarUser user={me} membership={membership} />
      </SidebarContainer>
    </SidebarDrawer>
  );
}

Sidebar.displayName = 'Sidebar';
export default Sidebar;
